import React from 'react';
import './FormRowAtom.scss';

export type FormRowAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type FormRowJustify = 'start' | 'center' | 'end' | 'between' | 'around';
export type FormRowGap = 'none' | 'small' | 'normal' | 'large';

export interface FormRowAtomProps {
  /** Content to render in the row */
  children: React.ReactNode;
  /** Vertical alignment of items */
  align?: FormRowAlign;
  /** Horizontal distribution of items */
  justify?: FormRowJustify;
  /** Gap between items */
  gap?: FormRowGap;
  /** Whether items should wrap to next line */
  wrap?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * FormRowAtom
 *
 * A horizontal flex container for laying out form elements in a row.
 * Provides consistent spacing and alignment.
 *
 * @example
 * <FormRowAtom align="center" gap="normal">
 *   <span>Every</span>
 *   <InputNumberAtom value={1} />
 *   <DropdownAtom options={frequencyOptions} />
 * </FormRowAtom>
 */
export const FormRowAtom: React.FC<FormRowAtomProps> = ({
  children,
  align = 'center',
  justify = 'start',
  gap = 'normal',
  wrap = false,
  className = '',
  'data-testid': testId,
}) => {
  const rootClasses = [
    'form-row',
    `form-row--align-${align}`,
    `form-row--justify-${justify}`,
    `form-row--gap-${gap}`,
    wrap && 'form-row--wrap',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} data-testid={testId}>
      {children}
    </div>
  );
};

export default FormRowAtom;
